import { message } from 'antd';
import { TFunction } from 'i18next';
import { ApolloError, FetchResult, MutationFunctionOptions } from '@apollo/client';
import { OrderType } from '../../../graphql/types/OrderType';
import {
    GenerateBatchShippingLabelPdfResponse,
    GenerateBatchShippingLabelPdfVariables,
    GenerateShippingLabelPdfResponse,
    GenerateShippingLabelPdfVariables,
} from '../../delivery/parcels/graphql/mutations/generateShippingLabelPdf';
import { printPdfFromBase64 } from '../../wms/bags/utils/printUtils';
import { showError } from '../../../utils/exceptionUtils';

type GenerateSinglePdf = (
    options: MutationFunctionOptions<
        GenerateShippingLabelPdfResponse,
        GenerateShippingLabelPdfVariables
    >
) => Promise<FetchResult<GenerateShippingLabelPdfResponse>>;

type GenerateBatchPdf = (
    options: MutationFunctionOptions<
        GenerateBatchShippingLabelPdfResponse,
        GenerateBatchShippingLabelPdfVariables
    >
) => Promise<FetchResult<GenerateBatchShippingLabelPdfResponse>>;

function handlePrintLabelError(error: unknown, t: TFunction): void {
    console.error('Print label failed:', error);
    const graphQLError = (error as ApolloError)?.graphQLErrors?.[0];
    const messageKey = graphQLError?.extensions?.messageKey as string | undefined;

    if (messageKey === 'errors.business.fbt.sortingOnlyBatchLabelNotSupported') {
        const details = graphQLError.extensions?.details as Record<string, unknown> | undefined;
        const trackingNumbers = details?.trackingNumbers;
        const formattedDetails =
            trackingNumbers && Array.isArray(trackingNumbers)
                ? { ...details, trackingNumbers: trackingNumbers.join(', ') }
                : details;
        message.error(String(t(messageKey, formattedDetails ?? {})));
        return;
    }

    if (messageKey) {
        showError(error as ApolloError);
        return;
    }

    message.error(t('central.parcels.messages.failedToGenerateBatchLabels'));
}

/**
 * 与总部包裹列表一致的打印面单逻辑（复用同一套 GraphQL Mutation + printPdfFromBase64）
 */
export async function printShippingLabelsForOrders(
    selectedRows: OrderType[],
    generateShippingLabelPdf: GenerateSinglePdf,
    generateBatchShippingLabelPdf: GenerateBatchPdf,
    t: TFunction
): Promise<void> {
    if (selectedRows.length === 1) {
        const order = selectedRows[0];
        const { data } = await generateShippingLabelPdf({
            variables: { orderId: parseInt(order.id, 10) },
        });
        if (data?.generateShippingLabelPdf) {
            const filename = `shipping_label_${order.trackingNumber || order.id}.pdf`;
            printPdfFromBase64(data.generateShippingLabelPdf, filename);
            message.success(
                t('central.parcels.messages.generatedBatchShippingLabel', { count: 1 })
            );
        }
        return;
    }

    const orderIds = selectedRows.map((order) => parseInt(order.id, 10));
    const { data } = await generateBatchShippingLabelPdf({
        variables: { orderIds },
    });

    if (data?.generateBatchShippingLabelPdf) {
        const trackingNumbers = selectedRows
            .map((order) => order.trackingNumber || order.id)
            .slice(0, 3);
        const filenameSuffix =
            trackingNumbers.length < selectedRows.length
                ? `${trackingNumbers.join('_')}_and_${selectedRows.length - trackingNumbers.length}_more`
                : trackingNumbers.join('_');
        const filename = `shipping_labels_${filenameSuffix}.pdf`;
        printPdfFromBase64(data.generateBatchShippingLabelPdf, filename);
        message.success(
            t('central.parcels.messages.generatedBatchShippingLabel', {
                count: selectedRows.length,
            })
        );
    }
}

export { handlePrintLabelError };
